import IconTrashBin from "assets/img/icon-trash-bin.svg?react"
import IconCopy from "assets/img/icon-copy.svg?react"

import React, { FunctionComponent } from "react"
import { useTranslation } from "react-i18next"
import cn from "classnames"

import { Grid, GridItem } from "components/Grid"
import { Flex } from "components/Flex"
import Text from "components/Text/Text"
import Button from "components/Button/Button"
import Select from "components/Select/Select"
import SelectSupplier from "components/Select/SelectSupplier"
import Input from "components/Input/Input"
import InputNumber from "components/InputNumber/InputNumber"
import InputDateTime from "components/InputDateTime/InputDateTime"
import FormField from "components/FormField/FormField"
import ValidationMessage from "components/ValidationMessage/ValidationMessage"
import AssetStatusTag from "components/AssetStatusTag/AssetStatusTag"
import { AssetOwnershipType, getAssetStatusLabel } from "models/Asset"
import { LmidModalMode } from "components/LmidModal/lmidModalMode"
import { allowedStatuses } from "./copyAssetMatrix"
import {
    CopyAssetErrorField,
    CopyAssetSharedKey,
    ICopyAssetLmidOverwrite,
    ICopyAssetRow,
    ICopyAssetRowError,
    ICopyAssetSharedValues,
    ICopyAssetState,
    effectiveValue,
    needsOwnerInput,
} from "./copyAssetRows"
import { COPY_ASSET_TEXT_FIELDS, ownershipAmountField } from "./copyAssetFieldDefs"
import CopyAssetNotesField from "./CopyAssetNotesField"
import CopyAssetLmidField from "./CopyAssetLmidField"
import { IApiLink } from "@/types"

export interface ICopyAssetRowCardProps {
    state: ICopyAssetState
    row: ICopyAssetRow
    rowNumber: number
    errors: ICopyAssetRowError[]
    isSelected: boolean
    canDuplicate: boolean
    canRemove: boolean
    articleNumber: string
    isInternalArticle: boolean
    takenLmids: string[]
    takenLmidCopyNumbers: Record<string, number>
    /** Currency shown after the amount fields, keyed by shared value. */
    amountCurrencies: Partial<Record<CopyAssetSharedKey, string>>
    onSelect(): void
    onDuplicate(): void
    onRemove(): void
    onSetRowValue(key: "serialNumber" | "serialNumberMachine" | "inventoryNo", value: string): void
    onSetOverride<K extends CopyAssetSharedKey>(key: K, value: ICopyAssetSharedValues[K]): void
    onSetLmidMode(lmidMode: LmidModalMode): void
    onAssignLmid(lmid: string, createAssetLink: IApiLink | null, overwrite: ICopyAssetLmidOverwrite | null): void
}

/**
 * Editable card of a single copy: its LMID, the always-individual fields, the
 * target status, and the shared fields it may override. A field holding an own
 * value is marked "only this copy" above its label; validation messages appear
 * directly below the field they belong to.
 */
const CopyAssetRowCard: FunctionComponent<React.PropsWithChildren<ICopyAssetRowCardProps>> = ({
    state,
    row,
    rowNumber,
    errors,
    isSelected,
    canDuplicate,
    canRemove,
    articleNumber,
    isInternalArticle,
    takenLmids,
    takenLmidCopyNumbers,
    amountCurrencies,
    onSelect,
    onDuplicate,
    onRemove,
    onSetRowValue,
    onSetOverride,
    onSetLmidMode,
    onAssignLmid,
}) => {
    const { t } = useTranslation()

    const errorFor = (field: CopyAssetErrorField) => errors.find(error => error.field === field)
    const overrideCountOnRow = Object.keys(row.overrides).length
    const isBorrowed = state.ownershipType === AssetOwnershipType.borrowed
    const textFields = [...COPY_ASSET_TEXT_FIELDS, ownershipAmountField(isBorrowed)]

    const lmidError = errorFor(CopyAssetErrorField.lmid)
    const status = effectiveValue(state, row, "status")

    const lmidTitle = row.lmidMode === LmidModalMode.requestLmid
        ? t('copyAsset.assignedOnSave')
        : row.lmidMode === LmidModalMode.enterLmidLater
            ? t('copyAsset.savedWithoutLmid')
            : row.lmid || undefined

    function ownValueNote(isOverridden: boolean) {
        if (!isOverridden) return undefined
        return (
            <Text type="pSmallest" className="copy-asset__own-note">
                {t('copyAsset.onlyThisCopy')}
            </Text>
        )
    }

    const fieldErrorByKey: Partial<Record<CopyAssetSharedKey, ICopyAssetRowError>> = {
        internalOrderNumber: errorFor(CopyAssetErrorField.internalOrderNumber),
        constructionYear: errorFor(CopyAssetErrorField.constructionYear),
        referenceNumberSale: errorFor(CopyAssetErrorField.referenceNumberSale),
        referenceNumberRental: errorFor(CopyAssetErrorField.referenceNumberRental),
        monthlyRentalCostValue: errorFor(CopyAssetErrorField.monthlyRentalCostValue),
        referenceValue: errorFor(CopyAssetErrorField.referenceValue),
        ownerName: errorFor(CopyAssetErrorField.ownerName),
    }

    return (
        <div
            className={cn("copy-asset__card p-2 border-bottom-grey-200", { "copy-asset__card--selected": isSelected })}
            onClick={onSelect}
            data-testid={`copy-asset-card-${rowNumber}`}>
            <Flex justifyContent="space-between" alignItems="center" gap="1.6rem" className="mb-1">
                <Flex alignItems="center" gap="0.8rem">
                    <span className={cn("copy-asset__number", { "copy-asset__number--selected": isSelected })}>
                        {rowNumber}
                    </span>
                    <Text type="p" className="font-bold">
                        {t('copyAsset.copyNofM', { number: rowNumber, total: state.rows.length })}
                    </Text>
                    {overrideCountOnRow > 0 &&
                        <Text type="pSmallest" className="copy-asset__edited-chip px-1 round-sm">
                            {t('copyAsset.edited', { count: overrideCountOnRow })}
                        </Text>
                    }
                </Flex>
                <Flex alignItems="center" gap="0.4rem">
                    <Button
                        round
                        theme="grey"
                        ariaLabel={t('copyAsset.duplicateCopy')}
                        title={t('copyAsset.duplicateCopy')}
                        icon={<IconCopy />}
                        disabled={!canDuplicate}
                        onClick={onDuplicate}
                    />
                    <Button
                        round
                        theme="grey"
                        ariaLabel={t('copyAsset.deleteCopy')}
                        title={t('copyAsset.deleteCopy')}
                        icon={<IconTrashBin />}
                        disabled={!canRemove}
                        onClick={onRemove}
                    />
                </Flex>
            </Flex>
            <Grid columns="repeat(auto-fill, minmax(18rem, 1fr))" gap="0.8rem 1.6rem">
                <GridItem className="copy-asset__field">
                    <FormField label={t('common.lmid')}>
                        <div title={lmidError?.message ?? lmidTitle}>
                            <CopyAssetLmidField
                                state={state}
                                row={row}
                                rowNumber={rowNumber}
                                status={status}
                                isInvalid={!!lmidError}
                                articleNumber={articleNumber}
                                isInternalArticle={isInternalArticle}
                                takenLmids={takenLmids}
                                takenLmidCopyNumbers={takenLmidCopyNumbers}
                                onSetLmidMode={onSetLmidMode}
                                onAssignLmid={onAssignLmid}
                            />
                        </div>
                    </FormField>
                </GridItem>
                <GridItem className="copy-asset__field">
                    <FormField label={t('asset.serialNumber')}>
                        <Input
                            value={row.serialNumber}
                            placeholder={t('common.pleaseEnter')}
                            onChange={e => onSetRowValue("serialNumber", e.target.value)}
                            data-testid={`copy-asset-serial-${rowNumber}`}
                        />
                    </FormField>
                </GridItem>
                <GridItem className="copy-asset__field">
                    <FormField label={t('copyAsset.fields.serialNumberMachine')}>
                        <Input
                            value={row.serialNumberMachine}
                            placeholder={t('common.pleaseEnter')}
                            onChange={e => onSetRowValue("serialNumberMachine", e.target.value)}
                        />
                    </FormField>
                </GridItem>
                <GridItem className="copy-asset__field">
                    <FormField label={t('copyAsset.fields.inventoryNumber')}>
                        <Input
                            value={row.inventoryNo}
                            placeholder={t('common.pleaseEnter')}
                            onChange={e => onSetRowValue("inventoryNo", e.target.value)}
                        />
                    </FormField>
                </GridItem>
                <GridItem className="copy-asset__field">
                    {ownValueNote(row.overrides.status !== undefined)}
                    <FormField label={t('asset.assetStatus')}>
                        <Select
                            value={status}
                            options={allowedStatuses(state.sourceKind, state.ownershipType, row.lmidMode).map(option => ({
                                label: getAssetStatusLabel(option),
                                value: option,
                            }))}
                            optionTemplate={option => <AssetStatusTag status={option.value} />}
                            optionTemplateContext="all"
                            onChange={option => option && onSetOverride("status", option.value)}
                            isInModal
                            testIdPrefix={`copy-asset-status-${rowNumber}`}
                        />
                    </FormField>
                </GridItem>
                {needsOwnerInput(state) &&
                    <GridItem className="copy-asset__field">
                        {ownValueNote(row.overrides.ownerName !== undefined)}
                        <FormField label={t('copyAsset.fields.companyWhereBorrowedAsset')}>
                            <SelectSupplier
                                value={effectiveValue(state, row, "ownerName")}
                                isInvalid={!!fieldErrorByKey.ownerName}
                                isInModal
                                testIdPrefix={`copy-asset-ownerName-${rowNumber}`}
                                onChange={ownerName => onSetOverride("ownerName", ownerName ?? "")}
                            />
                            {!!fieldErrorByKey.ownerName &&
                                <ValidationMessage>{fieldErrorByKey.ownerName.message}</ValidationMessage>
                            }
                        </FormField>
                    </GridItem>
                }
                {textFields.map(field => {
                    const error = fieldErrorByKey[field.key]
                    if (field.key === "internalNotes") {
                        return (
                            <GridItem key={field.key} className="copy-asset__field">
                                {ownValueNote(row.overrides.internalNotes !== undefined)}
                                <CopyAssetNotesField
                                    label={t(field.labelKey)}
                                    value={effectiveValue(state, row, "internalNotes")}
                                    onChange={notes => onSetOverride("internalNotes", notes)}
                                    testId={`copy-asset-internalNotes-${rowNumber}`}
                                />
                            </GridItem>
                        )
                    }
                    if (field.isAmount) {
                        return (
                            <GridItem key={field.key} className="copy-asset__field">
                                {ownValueNote(row.overrides[field.key] !== undefined)}
                                <FormField label={t(field.labelKey)}>
                                    <InputNumber
                                        value={effectiveValue(state, row, field.key)}
                                        minDecimals={2}
                                        maxDecimals={2}
                                        placeholder={t('common.pleaseEnter')}
                                        isSubmitted
                                        isInvalid={!!error}
                                        after={<Text className="px-2">{amountCurrencies[field.key]}</Text>}
                                        onChange={value => onSetOverride(field.key, value)}
                                        data-testid={`copy-asset-${field.key}-${rowNumber}`}
                                    />
                                    {!!error &&
                                        <ValidationMessage>{error.message}</ValidationMessage>
                                    }
                                </FormField>
                            </GridItem>
                        )
                    }
                    return (
                        <GridItem key={field.key} className="copy-asset__field">
                            {ownValueNote(row.overrides[field.key] !== undefined)}
                            <FormField label={t(field.labelKey)}>
                                <Input
                                    value={effectiveValue(state, row, field.key)}
                                    maxLength={field.maxLength}
                                    placeholder={t('common.pleaseEnter')}
                                    isInvalid={!!error}
                                    onChange={e => onSetOverride(field.key, e.target.value)}
                                    data-testid={`copy-asset-${field.key}-${rowNumber}`}
                                />
                                {!!error &&
                                    <ValidationMessage>{error.message}</ValidationMessage>
                                }
                            </FormField>
                        </GridItem>
                    )
                })}
                <GridItem className="copy-asset__field">
                    {ownValueNote(row.overrides.availabilityDate !== undefined)}
                    <FormField label={t('asset.expectedToBeAvailableOn')}>
                        <InputDateTime
                            type="date"
                            date={effectiveValue(state, row, "availabilityDate")}
                            utc
                            onDateChange={date => onSetOverride("availabilityDate", date)}
                        />
                    </FormField>
                </GridItem>
            </Grid>
        </div>
    )
}

export default CopyAssetRowCard
